<script lang="ts">
	import { appName } from "../../../../core/schema/names/appName.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import Layout from "../components/Layout.svelte";
	import StaticPicture from "../../../shared/components/Picture/StaticPicture.svelte";
	import type { ProductProps } from "./ProductProps.js";
	import logo from "../../../../core/assets/logo.svg";
	import ProductMobileGallery from "../../../shared/components/ProductMobileGallery.svelte";
	import DynamicPicture from "../../../shared/components/Picture/DynamicPicture.svelte";
	import ProductDesktopGallery from "../../../shared/components/ProductDesktopGallery.svelte";
	import Content from "../../../shared/components/Content.svelte";
	import ReferencesSlider from "../../../shared/components/Sliders/ReferencesSlider.svelte";
	import Card from "../../../shared/components/Card.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Product.translations.json";
	import ContactAndServiceCTA from "../../../shared/components/Contact/ContactAndServiceCTA.svelte";
	import type { ProductAdditionalInfo } from "../../../../core/schema/Product.js";
	import Button from "../../../shared/components/Button.svelte";
	import Specs from "../../../shared/components/Specs.svelte";
	import Link from "../../../shared/components/Link.svelte";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import arrowDownPrimary from "../../../../core/assets/icons/arrow-down-primary.svg?raw";
	import Breadcrumbs from "../../../shared/components/Breadcrumbs.svelte";
	import { getProductDetailPath } from "../../../../core/schema/paths/getProductDetailPath.js";
	import { getContext, setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";
	import { VertiType } from "../../../../core/schema/VertiType.js";
	import { Category } from "../../../../core/schema/Category.js";
	import { vertiTypeTargetFromSourceTenantIdMappings } from "../../../../core/schema/vertiTypeTargetFromSourceTenantIdMappings.js";

	type $$Props = ProductProps;
	const { product, category, references, categoryProducts, breadcrumbs, relatedContact } = $$restProps as $$Props;
	const { STORAGE_URL, TENANT_LOCALE, TENANT_ID, VERTINODE_WEB_URL } = getGlobalSettings();
	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);

	// create function to balance an array to roughly same length
	function balance<Item>(array: Item[]): [Item[], Item[]] {
		const first = array.slice(0, Math.ceil(array.length / 2));
		const second = array.slice(Math.ceil(array.length / 2));
		return [first, second];
	}

	$: groupedSpecs = product.specifications.reduce<{
		onlyValue: ProductAdditionalInfo[];
		nameAndValue: ProductAdditionalInfo[];
	}>(
		(group, spec) => {
			if (spec.value && spec.name) {
				group.nameAndValue.push(spec);
			} else if (spec.value) {
				group.onlyValue.push(spec);
			}
			return group;
		},
		{
			onlyValue: [],
			nameAndValue: [],
		},
	);
	$: balancedSpecs = [...balance(groupedSpecs.nameAndValue), groupedSpecs.onlyValue].filter(
		(array) => array.length > 0,
	);

	const maxAmountBeforeShowMore = 5;
	let specificationsShowMore = product.specifications.length <= maxAmountBeforeShowMore;

	const translate = getTranslate(translations);
	setContext(thumbnailImageContextKey, `${STORAGE_URL}/${product.primaryImage.path}`);
</script>

<Layout>
	<Breadcrumbs pageSlug={pageSlug[TENANT_LOCALE] ?? ""} {breadcrumbs} />
	<section class="container">
		<div>
			<div class="{product.keyProperties.length === 0 ? 'gap-4' : 'gap-9'} mt-4 flex flex-col lg:gap-32">
				<div class="flex flex-col md:gap-10 lg:flex-row lg:gap-[4rem]">
					<ProductDesktopGallery {product} media={product.images} class="hidden lg:block" />
					<div>
						<div class="mb-5 flex flex-col items-start gap-0 px-4 pt-4 md:px-0 md:pt-0 lg:mb-8">
							<h1 class="m-0 p-0 text-[2rem] font-bold leading-9 md:text-xl md:leading-[3.25rem]">
								{nbspify(product.name)}
							</h1>
							{#if product.subheadline}
								<h2 class="m-0 p-0 text-[2rem] leading-9 md:text-xl md:leading-[3.25rem]">
									{nbspify(product.subheadline)}
								</h2>
							{/if}
						</div>

						{#if product.perex}
							<div class="mb-8 flex flex-col gap-4 px-4 md:px-0 lg:mb-10 lg:w-3/4">
								<p class="text-3xs leading-4 md:text-base md:leading-6">{nbspify(product.perex)}</p>

								{#if product.content}
									<Link
										aria-label={translate("moreAria", {}, true)}
										asButton
										variant="transparent"
										href="#content"
										class="{product.keyProperties.length === 0 &&
											'hidden lg:flex'} text-primary text-3xs ml-auto justify-center font-semibold uppercase md:mt-2 md:text-xs"
										icon={arrowRightLong}
									>
										{translate("more")}
									</Link>
								{/if}
							</div>
						{/if}

						{#if product.keyProperties.length > 0}
							<div class="flex flex-col gap-4">
								<span class="font-spaceGrotesk text-2md px-4 font-medium uppercase md:px-0 lg:leading-7">
									{translate("keyProps")}
								</span>
								<div class="flex flex-col gap-2">
									{#each product.keyProperties as keyProps}
										<div class="flex w-full flex-col gap-1 rounded-lg bg-gray-300 p-4">
											<span class="text-2xs text-primary font-semibold uppercase leading-5"
												>{nbspify(keyProps.name)}</span
											>
											<span class="text-2xs font-normal leading-5">{nbspify(keyProps.value)}</span>
										</div>
									{/each}
								</div>
							</div>
						{/if}
					</div>
				</div>
				{#if product.images.length === 0}
					<div class="lg:hidden">
						<div
							class="bg-secondaryBlack-40 flex aspect-square h-auto max-h-[37rem] w-full max-w-[37rem] items-center justify-center rounded-md"
						>
							<StaticPicture
								alt="Logo {appName}"
								image={logo}
								class="w-28 opacity-50 sm:w-36"
								loading="eager"
								width={768}
								height={768}
							/>
						</div>
					</div>
				{:else if product.images.length > 1}
					<ProductMobileGallery {product} media={product.images} class="lg:hidden" />
				{:else}
					{@const [image] = product.images}
					{#if image}
						<div class="lg:hidden">
							<DynamicPicture
								imgClass="rounded-md aspect-square object-contain"
								{image}
								width={768}
								height={768}
								alt="{translate('photo')} – {product.name}"
								lightboxId={product.id}
								lightboxWidth={800}
								bucket="storage"
								loading="eager"
								sourcesets={{
									"<md": [490, 0],
									">md": [650, 0],
									">lg": [456, 0],
									">xl": [584, 0],
								}}
							/>
						</div>
					{/if}
				{/if}
				{#if product.specifications.length > 0}
					<div class="flex flex-col items-start gap-8 lg:gap-14">
						<div class="flex w-full justify-start">
							<h2
								class="font-spaceGrotesk text-2lg m-0 w-1/2 px-4 text-start font-normal leading-[2.1875rem] md:px-0 lg:leading-[3.3rem]"
							>
								{translate("specs")}
							</h2>
						</div>
						<div
							class="hidden w-full gap-2 xl:grid"
							style:grid-template-columns="repeat({balancedSpecs.length}, 1fr)"
							style:grid-template-rows="repeat({Math.max(...balancedSpecs.map((specs) => specs.length))}, 1fr)"
						>
							{#each balancedSpecs as specs}
								<div class="row-span-full grid grid-rows-subgrid">
									{#each specs as spec}
										<Specs {spec} />
									{/each}
								</div>
							{/each}
						</div>
						<div
							class="hidden w-full grid-cols-2 gap-2 md:grid xl:hidden"
							style:grid-template-columns="repeat({Object.values(groupedSpecs).filter((array) => array.length > 0)
								.length}, 1fr)"
							style:grid-template-rows="repeat({Math.max(...Object.values(groupedSpecs).map((specs) => specs.length))},
							1fr)"
						>
							{#each [groupedSpecs.nameAndValue, groupedSpecs.onlyValue] as specs}
								<div class="row-span-full grid grid-rows-subgrid">
									{#each specs as spec}
										<Specs {spec} />
									{/each}
								</div>
							{/each}
						</div>
						<div class="flex w-full flex-col gap-2 md:hidden">
							{#each [...groupedSpecs.nameAndValue, ...groupedSpecs.onlyValue].slice(0, specificationsShowMore ? undefined : maxAmountBeforeShowMore) as spec}
								<div class="flex w-full justify-between gap-2 rounded-lg bg-gray-300 p-4">
									<Specs {spec} />
								</div>
							{/each}
							{#if !specificationsShowMore}
								<div class="relative">
									<div class="bg-shadowElement absolute -top-[235%] h-28 w-full"></div>
									<Button
										variant="custom"
										class="text-primary shadow-custom-white-up border-primary !text-4xs flex w-full items-center justify-between border-2 bg-gray-300 !p-4 font-semibold uppercase"
										icon={arrowDownPrimary}
										on:click={() => {
											specificationsShowMore = true;
										}}
									>
										{translate("showMore")}
									</Button>
								</div>
							{/if}
						</div>
					</div>
				{/if}
				<div class="flex flex-col md:items-center">
					{#if product.content}
						<div class="mb-8 lg:mb-12 lg:w-[50rem]" id="content">
							<Content content={nbspify(product.content)} />
						</div>
					{/if}
					{#if category === Category.VertinodeAi}
						<Link
							asButton
							variant="primary"
							class="w-auto"
							icon={arrowRightLong}
							href={VERTINODE_WEB_URL[
								vertiTypeTargetFromSourceTenantIdMappings[VertiType.Node][VertiType.Flex][TENANT_ID]
							]}
						>
							{nbspify(translate("toVertiNode"))}
						</Link>
					{/if}
				</div>
				<div>
					<ContactAndServiceCTA {relatedContact} />
					{#if references.length > 0}
						<ReferencesSlider vertiType={VertiType.Flex} {references} />
					{/if}
					{#if categoryProducts.length > 0}
						<div class="mt-16 flex flex-col items-center gap-10">
							<span class="text-lg lg:text-xl">{translate("anotherProductsFromCategory")}</span>
							<div
								class="gap-2 {categoryProducts.length < 3
									? 'lg:grid-cols-2 xl:w-2/3'
									: 'grid-cols-1 lg:grid-cols-3'} {categoryProducts.length < 2 ? 'flex justify-center' : 'grid'}"
							>
								{#each categoryProducts as product}
									<Card
										title={product.name}
										description={product.perex}
										urlSlug={getProductDetailPath(category, product)}
										image={product.primaryImage}
										squaredImage={categoryProducts.length < 3}
										containImage
									/>
								{/each}
							</div>
						</div>
					{/if}
				</div>
			</div>
		</div>
	</section>
</Layout>
